import { useCallback, useEffect, useState } from "react";

import { Button, Col, Row } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import Title from "antd/es/typography/Title";

import usePageLoading from "hooks/usePageLoading";

import {
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonHeading,
  SkeletonInput,
  SlowStateBanner,
  TableSkeleton,
} from "js/components/Loading";
import TableSearch from "js/components/TableSearch";
import { ModernTenantModal } from "js/components/Tenants/ModernTenantModal";
import TenantsTable from "js/components/Tenants/TenantsTable";
import { PaginationTable, TenantPerson } from "js/components/types";

import "styles/components/Dashboard/TableDashboard.scss";

import { useGetTenantsPaginated } from "../../../hooks/useGetTenantsPaginated";
import { toast } from "../../../utils/toast";

/**
 * Referencia estable para el caso "sin datos".
 *
 * Un literal `[]` es un array NUEVO en cada evaluacion, asi que
 * `setFilteredData(data || [])` cambiaba el estado de verdad incluso cuando no
 * habia nada que cambiar, re-renderizando el arbol en bucle junto con el
 * useEffect de TableSearch (MB-APP-EXPRESS-172 / -15K).
 *
 * NO MUTAR: se comparte entre renders. Toda la app trata estas listas como
 * inmutables (se filtran con `.filter`, que copia).
 */
const EMPTY_TENANTS: TenantPerson[] = [];

const TenantsDashboard = () => {
  const screen = useBreakpoint();
  const [filteredData, setFilteredData] = useState<TenantPerson[]>([]);
  const [isTenantModalOpen, setIsTenantModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<
    TenantPerson | undefined
  >();
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  // Fetch tenants using TanStack Query hook
  const {
    data,
    isLoading,
    error,
    refetch: refetchTenants,
  } = useGetTenantsPaginated({ pagination });

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void refetchTenants();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <Button
          size="small"
          style={{
            borderColor: "#fcd34d",
            color: "#92400e",
            backgroundColor: "#fff7ed",
          }}
          onClick={handleRetryFetch}
        >
          Reintentar
        </Button>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-4">
      <SkeletonHeading className="h-9 w-56" />
    </div>
  );

  const actionsSkeleton = (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between lg:gap-0">
      <SkeletonInput className="lg:w-[30%]" />
      <SkeletonBlock className="h-10 w-full rounded-md lg:w-[20%]" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-4">
      <TableSkeleton columns={5} rows={6} />
    </div>
  );

  useEffect(() => {
    if (error) {
      toast.error("Error al cargar inquilinos. Por favor, inténtelo de nuevo.");
    }
  }, [error]);

  // Update pagination total and filtered data when data changes
  useEffect(() => {
    if (data) {
      // Idempotente a proposito: `{ ...prev }` siempre crea un objeto nuevo, y
      // `pagination` viaja DENTRO del queryKey (hooks/queryKeys.ts), asi que
      // escribirlo sin necesidad no solo re-renderiza: cambia la clave y
      // dispara un refetch de red extra.
      setPagination((prev) =>
        prev.total === data.length ? prev : { ...prev, total: data.length },
      );
      setFilteredData(data);
    }
  }, [data]);

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (!searchValue) {
        setFilteredData(data ?? EMPTY_TENANTS);
        return;
      }

      if (!data || !data.length) {
        return;
      }

      const lowercasedValue = searchValue.toLowerCase();
      const filtered = data.filter((tenant) => {
        const { name, first_surname, second_surname, email } = tenant;
        return (
          name?.toLowerCase().includes(lowercasedValue) ||
          first_surname?.toLowerCase().includes(lowercasedValue) ||
          second_surname?.toLowerCase().includes(lowercasedValue) ||
          email?.toLowerCase().includes(lowercasedValue)
        );
      });

      setFilteredData(filtered);
    },
    [data],
  );

  // Memoizado y con referencia estable: es el `onClear` que detonaba el bucle.
  const handleClearSearch = useCallback(() => {
    setFilteredData(data ?? EMPTY_TENANTS);
  }, [data]);

  const handleEditTenant = (tenant: TenantPerson) => {
    setSelectedTenant(tenant);
    setIsTenantModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsTenantModalOpen(false);
    setSelectedTenant(undefined);
  };

  return (
    <PageLoadingContainer
      showSkeleton={showSkeleton}
      slowState={slowStateBanner}
      headerSkeleton={headerSkeleton}
      actionsSkeleton={actionsSkeleton}
      contentSkeleton={contentSkeleton}
    >
      <Row
        gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
        justify="start"
        align="middle"
      >
        <Col xs={24} sm={24} md={12} lg={6}>
          <Title level={1} style={{ marginBottom: 5 }}>
            Inquilinos
          </Title>
        </Col>
      </Row>
      <Row
        gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
        align="middle"
        style={{ marginBottom: 15, marginTop: 10 }}
      >
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={24}
          style={{
            display: "flex",
            flexDirection: !screen.lg ? "column" : "row",
            justifyContent: "space-between",
            gap: !screen.lg ? 10 : 0,
          }}
        >
          <div style={{ width: !screen.lg ? "100%" : "30%" }}>
            <TableSearch
              reapplyOn={data}
              screen={screen}
              onSearch={handleSearch}
              onClear={handleClearSearch}
              label="inquilino(s)"
            />
          </div>
          <Button
            type="primary"
            block
            onClick={() => setIsTenantModalOpen(true)}
            style={{ width: !screen.lg ? "100%" : "20%" }}
          >
            Crear Inquilino
          </Button>
        </Col>
      </Row>
      <TenantsTable
        data={data ?? EMPTY_TENANTS}
        setData={() => {}} // No-op: data managed by TanStack Query
        filteredData={
          filteredData.length > 0 ? filteredData : (data ?? EMPTY_TENANTS)
        }
        setFilteredData={setFilteredData}
        pagination={pagination}
        setPagination={setPagination}
        loading={isLoading}
        fromOrganization={false}
        onEditTenant={handleEditTenant}
      />
      <ModernTenantModal
        isOpen={isTenantModalOpen}
        onOpenChange={handleCloseModal}
        onSuccess={() => {
          void refetchTenants();
        }}
        selectedTenant={selectedTenant}
      />
    </PageLoadingContainer>
  );
};

export default TenantsDashboard;
